import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { navigateTo } from '../../../libs/history';
import { getTrackedInternalLinkUrl } from '../../../libs/tracking';
import { formatCountdownMsTime } from '../../../libs/utils';
import { useTrackedImpression } from '../../../scripts/hooks/useTrackedImpression';
import { asset } from '../../../services/assetResolverService';
import { useMedia } from '../../grid/hooks';
import { Link, TrackedLink } from '../../link';
import CountdownTimer from '../../presentational/countdownTimer';
import { Typography } from '../../typography';

type TBreakTheMetaMnS1UpsellProps = {
  className?: string;
};

type TBtmStatus = 'registration' | 'live' | 'ended';
type TBtmClickPart = 'body' | 'button';

type TUpsellTranslations = {
  registrationHeadline: string;
  registrationLine: string;
  countdownStartsLabel: string;
  liveHeadline: string;
  liveLine: string;
  countdownEndsLabel: string;
  registrationButton: string;
  liveButton: string;
  logoAlt: string;
};

type TBtmUpsellConfig = {
  eventStartDate: string;
  eventEndDate: string;
  eventUrl: string;
  prizeLink: string;
  logoPath: string;
  logoWidth: number;
  logoHeight: number;
  translations: TUpsellTranslations;
};

// Verbiage flips registration -> live at EVENT_START_AT. The live "Ends in" countdown ticks to
// EVENT_END_AT, which index.tsx also imports as the rotation `endAt` (single source of truth).
export const EVENT_START_AT = '2026-07-14T15:00:00Z';
export const EVENT_END_AT = '2026-07-21T15:00:00Z';

const NAMESPACE = 'dyn_btm_mn_s1_upsell';
const ANALYTICS_PREFIX = 'btm_mn_s1';
const ANALYTICS_KEY = 'midnight_season_1';

// The specs players CAN play in Break the Meta MN1, shown as the scrolling icon marquee.
// The DB (events_bracket_spec_restrictions) stores the RESTRICTED/banned specs (max_allowed_count; 0 = banned),
// so the playable set is the COMPLEMENT: all current specs MINUS the base event bracket's restricted list
// (bracket `break-the-meta-midnight-season-1`, id 357 -> 17 restricted, 23 playable, pulled from the DB).
// Only the base event's restrictions count — the daily `btm-bounty-day-*` brackets are intentionally ignored.
// NOTE: hardcoded snapshot (the component can't query the DB) — re-verify against the DB before launch.
const ELIGIBLE_SPECS: { classSlug: string; specSlug: string }[] = [
  { classSlug: 'death-knight', specSlug: 'blood' },
  { classSlug: 'death-knight', specSlug: 'frost' },
  { classSlug: 'demon-hunter', specSlug: 'havoc' },
  { classSlug: 'demon-hunter', specSlug: 'vengeance' },
  { classSlug: 'druid', specSlug: 'balance' },
  { classSlug: 'druid', specSlug: 'feral' },
  { classSlug: 'druid', specSlug: 'restoration' },
  { classSlug: 'evoker', specSlug: 'devastation' },
  { classSlug: 'evoker', specSlug: 'preservation' },
  { classSlug: 'hunter', specSlug: 'marksmanship' },
  { classSlug: 'mage', specSlug: 'arcane' },
  { classSlug: 'mage', specSlug: 'fire' },
  { classSlug: 'monk', specSlug: 'windwalker' },
  { classSlug: 'paladin', specSlug: 'holy' },
  { classSlug: 'paladin', specSlug: 'protection' },
  { classSlug: 'priest', specSlug: 'discipline' },
  { classSlug: 'priest', specSlug: 'holy' },
  { classSlug: 'rogue', specSlug: 'assassination' },
  { classSlug: 'rogue', specSlug: 'outlaw' },
  { classSlug: 'shaman', specSlug: 'enhancement' },
  { classSlug: 'warlock', specSlug: 'affliction' },
  { classSlug: 'warlock', specSlug: 'destruction' },
  { classSlug: 'warrior', specSlug: 'protection' }
];

// Rendered twice so the marquee track loops seamlessly (translateX(-50%)).
const MARQUEE_SPECS = [...ELIGIBLE_SPECS, ...ELIGIBLE_SPECS];

const SHARED_CONFIG = {
  eventStartDate: EVENT_START_AT,
  eventEndDate: EVENT_END_AT,
  eventUrl: '/events/break-the-meta-midnight-season-1/btm-event-info',
  prizeLink: 'https://www.wowhead.com/item=272339/umbral-champions-illustrious-banner',
  // Break the Meta - Midnight S1 "META" wordmark (transparent PNG, 2:1 aspect).
  logoPath: '/images/events/expansion11/season-mn-1-break-the-meta/btm_logo.png',
  logoWidth: 168,
  logoHeight: 84
};

const EN_TRANSLATIONS: TUpsellTranslations = {
  registrationHeadline: 'Break the Meta Registration is OPEN',
  registrationLine:
    "Ready to shake up the meta? 💥 <0>Register now</0> and battle for glory, prizes, and the exclusive <1>Umbral Champion's Illustrious Banner</1>!",
  countdownStartsLabel: 'Starts in',
  liveHeadline: 'Break the Meta is LIVE!',
  liveLine:
    "🔥 Run off-meta keys, climb the ranks, and claim the <1>Umbral Champion's Illustrious Banner</1> before time runs out!",
  countdownEndsLabel: 'Ends in',
  registrationButton: 'Register Now',
  liveButton: 'View Leaderboards',
  logoAlt: 'Break the Meta logo'
};

const ZH_TRANSLATIONS: TUpsellTranslations = {
  registrationHeadline: '专精突破现已开放报名',
  registrationLine: '准备好突破专精了吗？💥 <0>现在报名</0>，争夺荣耀、奖励和专属<1>幽影勇士的卓越旗帜</1>！',
  countdownStartsLabel: '距开始',
  liveHeadline: '专精突破正式开始！',
  liveLine: '🔥 用冷门专精限时钥石，冲榜天梯，在结束前拿下<1>幽影勇士的卓越旗帜</1>！',
  countdownEndsLabel: '距结束',
  registrationButton: '现在报名',
  liveButton: '查看排行榜',
  logoAlt: '专精突破'
};

const UPSELL_CONFIGS: Record<string, TBtmUpsellConfig> = {
  en: { ...SHARED_CONFIG, translations: EN_TRANSLATIONS },
  'en-us': { ...SHARED_CONFIG, translations: EN_TRANSLATIONS },
  zh: {
    ...SHARED_CONFIG,
    // TODO(BTM MN1): confirm CN dates; defaulting to the global window for now.
    eventUrl: '/cn/events/break-the-meta-midnight-season-1-CN/btm-event-info',
    translations: ZH_TRANSLATIONS
  }
};

const panelStyle: React.CSSProperties = {
  border: '1px solid rgba(242, 30, 249, 0.6)',
  borderRadius: '0.25rem',
  backgroundColor: '#1b0628',
  backgroundImage: [
    'radial-gradient(circle at 86% 30%, rgba(242, 20, 249, 0.26) 0%, rgba(200, 28, 240, 0.08) 28%, rgba(200, 28, 240, 0) 54%)',
    'linear-gradient(90deg, rgba(15, 4, 24, 0.96) 0%, rgba(34, 8, 50, 0.92) 42%, rgba(70, 14, 96, 0.74) 100%)'
  ].join(', '),
  backgroundSize: 'cover',
  backgroundPosition: 'center right',
  backgroundRepeat: 'no-repeat',
  color: '#fff',
  cursor: 'pointer',
  columnGap: '0.75rem',
  overflow: 'hidden'
};

const logoWrapStyle: React.CSSProperties = {
  width: 176,
  minWidth: 176,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const logoStyle: React.CSSProperties = {
  display: 'block',
  width: 168,
  height: 84,
  objectFit: 'contain',
  filter: 'drop-shadow(0 8px 18px rgba(0, 0, 0, 0.34))'
};

const contentStyle: React.CSSProperties = {
  flex: '1 1 auto',
  maxWidth: '48rem',
  minWidth: 0
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '0.625rem',
  marginBottom: '0.38rem'
};

const headlinePromptStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.32rem',
  color: '#ffa1f2',
  lineHeight: 1.1
};

const countdownPillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  padding: '0.2rem 0.55rem',
  borderRadius: '999px',
  border: '1px solid rgba(242, 80, 249, 0.5)',
  background: 'rgba(26, 6, 40, 0.82)',
  color: '#f4b6ff',
  fontSize: '0.8125rem',
  fontWeight: 'bold'
};

const supportingLineStyle: React.CSSProperties = {
  lineHeight: 1.18,
  maxWidth: '39rem',
  color: '#efe7fb'
};

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: '#e61cec',
  minWidth: 156
};

// Fixed-width auto-scrolling marquee so the icon row never grows the panel / wraps the CTA.
const SPEC_MARQUEE_VIEWPORT_MAX_WIDTH = 520;
const specMarqueeViewportStyle: React.CSSProperties = {
  marginTop: '0.5rem',
  maxWidth: SPEC_MARQUEE_VIEWPORT_MAX_WIDTH,
  overflow: 'hidden',
  WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)',
  maskImage: 'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)'
};

const specMarqueeTrackStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'nowrap',
  width: 'max-content',
  willChange: 'transform'
};

const specIconStyle: React.CSSProperties = {
  width: 26,
  height: 26,
  marginRight: 8,
  flex: '0 0 auto',
  borderRadius: '4px',
  border: '1px solid rgba(242, 120, 249, 0.45)',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.4)'
};

// Animation lives on the class (not inline) so prefers-reduced-motion can disable it.
const SPEC_MARQUEE_CSS =
  '.btm-mn-s1-spec-marquee{animation:btmMnS1SpecMarquee 22s linear infinite}' +
  '@media (prefers-reduced-motion: reduce){.btm-mn-s1-spec-marquee{animation:none}}' +
  '@keyframes btmMnS1SpecMarquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}';

const ctaButtonClassName = [
  'slds-button',
  'slds-button--raiderio',
  'text-white',
  'slds-p-vertical--x-small',
  'slds-p-horizontal--small',
  'text-bold',
  'rio-text--title',
  'slds-text-heading--medium',
  'text-nowrap'
].join(' ');

export function getBreakTheMetaMnS1Status(eventStartDate: string, eventEndDate: string, now: number): TBtmStatus {
  if (now < Date.parse(eventStartDate)) {
    return 'registration';
  }
  if (now < Date.parse(eventEndDate)) {
    return 'live';
  }
  return 'ended';
}

function getStatusTransitionAt(eventStartDate: string, eventEndDate: string, status: TBtmStatus): number | null {
  if (status === 'registration') {
    return Date.parse(eventStartDate);
  }
  if (status === 'live') {
    return Date.parse(eventEndDate);
  }
  return null;
}

export function getBreakTheMetaMnS1UpsellClickTrackingPayload(
  status: TBtmStatus,
  part: TBtmClickPart,
  linkText: string,
  destinationPath: string,
  linkClasses = ''
) {
  return {
    link_url: getTrackedInternalLinkUrl(destinationPath),
    link_text: linkText,
    link_classes: linkClasses,
    link_id: `${ANALYTICS_PREFIX}:${part}:${ANALYTICS_KEY}`,
    content_type: 'upsell',
    content_id: `${ANALYTICS_PREFIX}:${ANALYTICS_KEY}`,
    upsell_id: ANALYTICS_PREFIX,
    upsell_part: part,
    upsell_variant: ANALYTICS_KEY,
    upsell_status: status,
    destination_path: destinationPath
  };
}

export function BreakTheMetaMidnightSeason1Upsell(props: TBreakTheMetaMnS1UpsellProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useMedia(['(max-width: 767px)'], [true], false);
  const isStackedCta = useMedia(['(max-width: 1099px)'], [true], false);
  const shouldStackCta = !isMobile && isStackedCta;
  const [currentTime, setCurrentTime] = useState(Date.now);

  Object.entries(UPSELL_CONFIGS).forEach(([lang, cfg]) => {
    if (!i18n.hasResourceBundle(lang, NAMESPACE)) {
      i18n.addResourceBundle(lang, NAMESPACE, cfg.translations);
    }
  });

  const config =
    UPSELL_CONFIGS[i18n.language] || UPSELL_CONFIGS[(i18n.language || '').split('-')[0]] || UPSELL_CONFIGS.en;

  const status = getBreakTheMetaMnS1Status(config.eventStartDate, config.eventEndDate, currentTime);
  const nextStatusTransitionAt = getStatusTransitionAt(config.eventStartDate, config.eventEndDate, status);
  const isLive = status === 'live';

  const syncStatus = () => setCurrentTime(Date.now());

  useEffect(() => {
    if (!nextStatusTransitionAt) {
      return undefined;
    }

    const timeoutMs = Math.max(nextStatusTransitionAt - currentTime, 0) + 1000;
    const timeout = window.setTimeout(() => setCurrentTime(Date.now()), timeoutMs);
    return () => window.clearTimeout(timeout);
  }, [currentTime, nextStatusTransitionAt]);

  const headlineText = t(`${NAMESPACE}:${isLive ? 'liveHeadline' : 'registrationHeadline'}`);
  const countdownLabelText = t(`${NAMESPACE}:${isLive ? 'countdownEndsLabel' : 'countdownStartsLabel'}`);
  const primaryButtonLabel = t(`${NAMESPACE}:${isLive ? 'liveButton' : 'registrationButton'}`);

  const panelImpressionRef = useTrackedImpression<HTMLDivElement>({
    onImpression: () => {
      RIO_TRACK_EVENT('event', 'upsell_impression', {
        content_type: 'upsell',
        content_id: `${ANALYTICS_PREFIX}:${ANALYTICS_KEY}`,
        upsell_id: ANALYTICS_PREFIX,
        upsell_variant: ANALYTICS_KEY,
        upsell_status: status,
        destination_path: config.eventUrl
      });
    }
  });

  const panelResponsiveStyle = {
    ...panelStyle,
    justifyContent: isMobile ? 'center' : undefined,
    rowGap: isMobile ? '0.5rem' : undefined
  };
  const logoWrapResponsiveStyle = {
    ...logoWrapStyle,
    width: isMobile ? 146 : logoWrapStyle.width,
    minWidth: isMobile ? 146 : logoWrapStyle.minWidth
  };
  const logoResponsiveStyle: React.CSSProperties = {
    ...logoStyle,
    width: isMobile ? 138 : logoStyle.width,
    height: isMobile ? 69 : logoStyle.height
  };
  const contentResponsiveStyle: React.CSSProperties = {
    ...contentStyle,
    textAlign: isMobile ? 'center' : 'left',
    maxWidth: shouldStackCta ? '100%' : contentStyle.maxWidth,
    margin: isMobile ? '0 auto' : undefined
  };
  const headerRowResponsiveStyle: React.CSSProperties = {
    ...headerRowStyle,
    justifyContent: isMobile ? 'center' : 'flex-start'
  };
  const supportingLineResponsiveStyle: React.CSSProperties = {
    ...supportingLineStyle,
    margin: isMobile ? '0 auto' : undefined
  };
  const buttonColumnStyle: React.CSSProperties = {
    flex: isMobile || shouldStackCta ? '0 0 100%' : '0 0 174px',
    width: isMobile || shouldStackCta ? '100%' : 174,
    textAlign: isMobile || shouldStackCta ? 'center' : undefined
  };
  const specMarqueeViewportResponsiveStyle: React.CSSProperties = {
    ...specMarqueeViewportStyle,
    marginLeft: isMobile ? 'auto' : undefined,
    marginRight: isMobile ? 'auto' : undefined
  };

  const trackUpsellClick = (e: React.MouseEvent<HTMLElement>, part: TBtmClickPart, linkText: string) => {
    const trackingPayload = getBreakTheMetaMnS1UpsellClickTrackingPayload(
      status,
      part,
      linkText,
      config.eventUrl,
      e.currentTarget.className || ''
    );

    RIO_TRACK_EVENT('event', 'upsell_click', trackingPayload);
    return trackingPayload;
  };

  const trackBodyClick = (e: React.MouseEvent<HTMLElement>) => {
    const trackingPayload = trackUpsellClick(e, 'body', headlineText);

    RIO_TRACK_EVENT('event', 'internal_click', trackingPayload);
  };

  const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    trackBodyClick(e);
    navigateTo(config.eventUrl, e);
  };

  const handleTextClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    trackBodyClick(e);
    navigateTo(config.eventUrl, e);
  };

  if (status === 'ended') {
    return null;
  }

  return (
    <div className={props.className}>
      <div
        ref={panelImpressionRef}
        className="slds-p-around--x-small slds-grid slds-grid--vertical-align-center slds-wrap"
        onClick={handlePanelClick}
        style={panelResponsiveStyle}
      >
        <div className="slds-col slds-shrink-none slds-align--absolute-center rio-m-top-mobi--small">
          <span style={logoWrapResponsiveStyle}>
            <img
              src={asset(config.logoPath, { width: 2 * config.logoWidth, height: 2 * config.logoHeight })}
              alt={t(`${NAMESPACE}:logoAlt`)}
              style={logoResponsiveStyle}
            />
          </span>
        </div>
        <div
          className="slds-col rio-m-top-mobi--small slds-p-horizontal--small"
          onClick={handleTextClick}
          style={contentResponsiveStyle}
        >
          <div style={headerRowResponsiveStyle}>
            <Typography.Title component="span" size="medium" strong style={headlinePromptStyle}>
              <i className={isLive ? 'fa fa-bolt' : 'fa fa-flag-checkered'} />
              {headlineText}
            </Typography.Title>
            <span style={countdownPillStyle}>
              <i className="fa fa-stopwatch" />
              <span>{countdownLabelText}</span>
              <CountdownTimer
                className="text-white"
                to={isLive ? config.eventEndDate : config.eventStartDate}
                formatter={formatCountdownMsTime}
                onComplete={syncStatus}
                tag="span"
              />
            </span>
          </div>
          <Typography.Text component="div" size="large" style={supportingLineResponsiveStyle}>
            <Trans
              i18nKey={`${NAMESPACE}:${isLive ? 'liveLine' : 'registrationLine'}`}
              components={[<b />, <TrackedLink href={config.prizeLink} target="_blank" className="text-bold" />]}
            />
          </Typography.Text>
          <div style={specMarqueeViewportResponsiveStyle} aria-label="Specs you can play in Break the Meta">
            <style>{SPEC_MARQUEE_CSS}</style>
            <div className="btm-mn-s1-spec-marquee" style={specMarqueeTrackStyle}>
              {MARQUEE_SPECS.map(({ classSlug, specSlug }, idx) => (
                <img
                  key={`${classSlug}_${specSlug}_${idx}`}
                  src={`https://cdn.raiderio.net/images/classes/spec_${classSlug}_${specSlug}.png`}
                  alt=""
                  loading="lazy"
                  style={specIconStyle}
                />
              ))}
            </div>
          </div>
        </div>
        <div
          className="slds-col slds-grow slds-p-vertical--small slds-p-right--small slds-align--absolute-center rio-m-top-mobi--small"
          style={buttonColumnStyle}
        >
          <Link
            className={ctaButtonClassName}
            to={config.eventUrl}
            as={`${ANALYTICS_PREFIX}:button`}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.stopPropagation();
              trackUpsellClick(e, 'button', primaryButtonLabel);
            }}
            style={primaryButtonStyle}
          >
            {primaryButtonLabel}
          </Link>
        </div>
      </div>
    </div>
  );
}
